import { createContext, FC, PropsWithChildren, useCallback, useContext } from "react";
import { useZLBoltInstance } from "@zerolight-core/libzl-react";
import { WebpageAnalytics } from "./WebpageAnalytics";
import { getInitialUrlArgs } from "./UrlArgsProvider";
import { analytics, JSONValue } from "@zerolight-core/iframe-communications";


export interface IAnalyticsContext {
	record?: (type: ["Configurator"|"Webpage", string], data?: {[key: string]: JSONValue}) => void
}

const AnalyticsContext = createContext<IAnalyticsContext>({});

// eslint-disable-next-line react-refresh/only-export-components
export const useAnalytics = ()  => useContext(AnalyticsContext)

let firstUserSession = false
let userID = window.localStorage.getItem("UserID")
if(!userID) {
	firstUserSession = true
	userID = crypto.randomUUID()
	window.localStorage.setItem("UserID", userID)
}

let browserTabID = window.sessionStorage.getItem("BrowserTabID")
if(!browserTabID) {
	browserTabID = crypto.randomUUID()
	window.sessionStorage.setItem("BrowserTabID", browserTabID)
}
const ExecutionContextID = crypto.randomUUID()

export const AnalyticsProvider : FC<PropsWithChildren> = ({children}) => {	
	const zlbolt = useZLBoltInstance()
	const {from, shareId, micrositeId, assetService} = getInitialUrlArgs()

	const record = useCallback((type: ["Configurator" | "Webpage", string], data?: {[key: string]: JSONValue}) => {
		console.info(`[Analytics] ${type[0]} ${type[1]}`, data)
		const tags = type[0] === "Configurator" ? ["action"] : undefined
		if(zlbolt?.cloudmine?.getCloudMineTransport()) {
			zlbolt.cloudmine.event({type, data: {...data}, ExecutionContextID, schemav: 1, tags})
		}
		else {
			//No transport is configured until after connecting zlbolt for the first time
			const jsonString = JSON.stringify({
				type,
				ZLBoltID : zlbolt.identifier,
				UserID : userID,
				BrowserTabID : browserTabID,
				ExecutionContextID: ExecutionContextID,
				FirstUserSession: firstUserSession,
				schemav: 1,
				data,
				tags
			});
			const validate = import.meta.env.DEV// || assetService !== "slatelive"
			const responsePromise = fetch(`https://accumulator-us-east-1-${assetService}.omnistream.zlthunder.net/public`, {
				method: 'POST',
				headers: {
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					...(validate ? {'X-CM-Diagnostic': '1'} : {})
				},
				body: jsonString
			})

			if(validate) {
				responsePromise.catch((reason: unknown) => {
					throw new Error(`Failed to send event ${type.join(",")} : ${reason}`)
				}).then((response) => {
					if(response.ok) {
						response.json().then(json => {
							if(json.validation?.length) {
								console.error(`Failed to send events ${type.join(",")}`, json.validation, data)
								throw new Error(`Failed to send event ${json.validation.join(",")}`)
							}
						})
					}
				})
			}
		}

		if(type[0] === "Configurator") {
			analytics(type[1], data)
		}
	}, [zlbolt, assetService])

	return (
		<AnalyticsContext.Provider value={{record: zlbolt?.cloudmine ? record : undefined}}>
			<WebpageAnalytics pageLoadDetails={{from, ShareID: shareId, MicrositeID: micrositeId}}/>
			{children}
		</AnalyticsContext.Provider>
	)
}